import { useQuery } from '@tanstack/react-query';
import { api } from '../trpc.js';
import {
  Alerta,
  Cartao,
  Carregando,
  ErroDeCarga,
  Indicador,
  Tabela,
  Vazio,
  formatarMoeda,
} from '../comum/ui.js';

/**
 * DASHBOARD POR COMPETÊNCIA (§8).
 *
 * A pergunta que esta tela responde em 3 segundos: "posso fechar esta
 * competência?". Por isso os bloqueantes vêm ANTES dos totais — um total
 * bonito com uma rejeição pendente é informação enganosa.
 */
export function Dashboard({ competencia }: { competencia: string }) {
  const consulta = useQuery({
    queryKey: ['dashboard', competencia],
    queryFn: () => api.dashboard.porCompetencia.query({ competencia }),
  });

  if (consulta.isLoading) return <Carregando o="dashboard" />;
  if (consulta.error) return <ErroDeCarga erro={consulta.error} />;

  const dados = consulta.data;
  if (!dados) return <Vazio mensagem="Sem dados para a competência." />;

  const bloqueantes = dados.alertas.filter((a) => a.severidade === 'BLOQUEANTE');
  const atencao = dados.alertas.filter((a) => a.severidade === 'ATENCAO');

  return (
    <div className="tela">
      <h1>Competência {competencia}</h1>

      {bloqueantes.length > 0 ? (
        <Cartao titulo={`${bloqueantes.length} pendência(s) bloqueante(s)`}>
          <p className="destaque-critico">
            O fechamento está bloqueado até que todas sejam resolvidas.
          </p>
          {bloqueantes.map((a, i) => (
            <Alerta key={`${a.codigo}-${i}`} severidade="BLOQUEANTE" titulo={a.codigo}>
              {a.mensagem}
            </Alerta>
          ))}
        </Cartao>
      ) : dados.totais ? (
        <Alerta severidade="INFO" titulo="Sem bloqueios">
          Nenhuma pendência bloqueante nesta competência.
        </Alerta>
      ) : null}

      {dados.totais ? (
        <>
          <Cartao titulo="Totais da folha" acao={<span className="sutil">versão {dados.versoes}</span>}>
            <div className="indicadores">
              <Indicador
                rotulo="Líquido a pagar"
                valor={formatarMoeda(dados.totais.liquido)}
                detalhe={`${dados.totais.quantidadeVinculos} colaborador(es)`}
              />
              <Indicador rotulo="Proventos" valor={formatarMoeda(dados.totais.proventos)} />
              <Indicador rotulo="Descontos" valor={formatarMoeda(dados.totais.descontos)} />
              <Indicador
                rotulo="INSS a recolher"
                valor={formatarMoeda(dados.totais.inssTotalRecolher)}
                detalhe="patronal + retido − salário-família"
              />
              <Indicador rotulo="FGTS" valor={formatarMoeda(dados.totais.fgts + dados.totais.fgts13)} />
              <Indicador rotulo="IRRF" valor={formatarMoeda(dados.totais.irrf + dados.totais.irrf13)} />
            </div>
          </Cartao>

          <Cartao titulo="Composição dos encargos">
            <Tabela cabecalhos={['Encargo', '#Base', '#Valor']}>
              <tr>
                <td>CPP (patronal)</td>
                <td className="numerica">{formatarMoeda(dados.totais.baseInss)}</td>
                <td className="numerica">{formatarMoeda(dados.totais.cpp)}</td>
              </tr>
              <tr>
                <td>RAT × FAP</td>
                <td className="numerica">{formatarMoeda(dados.totais.baseInss)}</td>
                <td className="numerica">{formatarMoeda(dados.totais.rat)}</td>
              </tr>
              <tr>
                <td>Terceiros</td>
                <td className="numerica">{formatarMoeda(dados.totais.baseInss)}</td>
                <td className="numerica">{formatarMoeda(dados.totais.terceiros)}</td>
              </tr>
              <tr>
                <td>INSS retido do segurado</td>
                <td className="numerica">{formatarMoeda(dados.totais.baseInss)}</td>
                <td className="numerica">{formatarMoeda(dados.totais.inssSegurado)}</td>
              </tr>
              <tr>
                <td>Salário-família (a compensar)</td>
                <td className="numerica">—</td>
                <td className="numerica">−{formatarMoeda(dados.totais.salarioFamilia)}</td>
              </tr>
            </Tabela>
            {dados.totais.cpp === 0 ? (
              <Alerta severidade="INFO" titulo="CPP zerada">
                A empresa é optante do Simples Nacional: a contribuição patronal é recolhida
                dentro do DAS, não sobre a folha.
              </Alerta>
            ) : null}
          </Cartao>
        </>
      ) : (
        <Vazio
          mensagem="Folha ainda não calculada nesta competência."
          sugestao="Aprove as entradas pendentes e rode o cálculo."
        />
      )}

      {atencao.length > 0 ? (
        <Cartao titulo={`${atencao.length} ponto(s) de atenção`}>
          {atencao.map((a, i) => (
            <Alerta key={`${a.codigo}-${i}`} severidade="ATENCAO" titulo={a.codigo}>
              {a.mensagem}
            </Alerta>
          ))}
        </Cartao>
      ) : null}
    </div>
  );
}
